import { FormOption } from 'common/form';
import { CampaignType, Channels } from 'generated-graphql/types';
import { AtsProvider } from 'services/integration/types';
import { RouterLink } from 'common/ui/RouterLink';

export type GetSourcingSharedCampaignTypeOptionsInput = {
  hasAtsJobId: boolean;
  hasIntegrationCredentials?: boolean;
  atsProvider: string | undefined | null;
  portalUrl: string | undefined | null;
  customJobPostingUrl: string | undefined | null;
  atsJobApplicationUrl: string | undefined | null;
  channelType: Channels;
};

export const SOURCING_UPLOAD_CV_IN_CONVERSATION_LABEL =
  'Uploading CV in conversation';
export const SOURCING_APPLICATION_VIA_A_URL_LABEL = 'Job application URL';

// ATS providers that support uploading a CV in conversation and syncing it
// back to the ATS via Kombo.
export const CV_IN_CONVERSATION_SUPPORTED_ATS_PROVIDERS: string[] = [
  AtsProvider.lever,
  AtsProvider.pinpoint,
  AtsProvider.bullhorn
];

export const getSourcingSharedCampaignTypeOptions = ({
  hasAtsJobId,
  hasIntegrationCredentials,
  atsProvider,
  portalUrl,
  customJobPostingUrl,
  atsJobApplicationUrl
}: GetSourcingSharedCampaignTypeOptionsInput): FormOption[] => {
  const options: FormOption[] = [];

  if (!hasAtsJobId) {
    options.push({
      label: SOURCING_UPLOAD_CV_IN_CONVERSATION_LABEL,
      value: CampaignType.RequestCvOutreach
    });

    if (portalUrl) {
      options.push({
        label: SOURCING_APPLICATION_VIA_A_URL_LABEL,
        value: CampaignType.CustomJobApplicationUrlOutreach,
        endItem: (
          <RouterLink to={portalUrl} target="_blank">
            Preview link
          </RouterLink>
        )
      });
    } else if (!hasIntegrationCredentials) {
      options.push({
        label: SOURCING_APPLICATION_VIA_A_URL_LABEL,
        value: CampaignType.CustomJobApplicationUrlOutreach,
        endItem: customJobPostingUrl ? (
          <RouterLink to={customJobPostingUrl} target="_blank">
            Preview link
          </RouterLink>
        ) : undefined
      });
    }

    return options;
  }

  if (
    atsProvider &&
    CV_IN_CONVERSATION_SUPPORTED_ATS_PROVIDERS.includes(atsProvider)
  ) {
    options.push({
      label: SOURCING_UPLOAD_CV_IN_CONVERSATION_LABEL,
      value: CampaignType.RequestCvOutreach
    });
  }

  if (atsJobApplicationUrl) {
    options.push({
      label: SOURCING_APPLICATION_VIA_A_URL_LABEL,
      value: CampaignType.AtsJobApplicationUrlOutreach,
      endItem: (
        <RouterLink to={atsJobApplicationUrl} target="_blank">
          Preview link
        </RouterLink>
      )
    });
  } else {
    options.push({
      label: SOURCING_APPLICATION_VIA_A_URL_LABEL,
      value: CampaignType.CustomJobApplicationUrlOutreach,
      endItem: customJobPostingUrl ? (
        <RouterLink to={customJobPostingUrl} target="_blank">
          Preview link
        </RouterLink>
      ) : undefined
    });
  }

  return options;
};

/**
 * Checks if only the CustomJobApplicationUrlOutreach option is available
 * (URL-only mode where radio button should be hidden)
 */
export const isSourcingSharedUrlOnlyMode = (
  campaignTypeOptions: FormOption[]
): boolean => {
  return (
    campaignTypeOptions.length === 1 &&
    campaignTypeOptions[0].value ===
      CampaignType.CustomJobApplicationUrlOutreach
  );
};

/**
 * Checks if in URL-only mode AND a URL already exists (preview mode)
 * A URL can come from either portalUrl or customJobPostingUrl
 */
export const isSourcingSharedUrlPreviewMode = (
  campaignTypeOptions: FormOption[],
  portalUrl: string | undefined | null,
  customJobPostingUrl: string | undefined | null
): boolean => {
  return (
    isSourcingSharedUrlOnlyMode(campaignTypeOptions) &&
    (!!portalUrl || !!customJobPostingUrl)
  );
};

/**
 * Checks if in URL-only mode AND no URL exists yet (add mode)
 */
export const isSourcingSharedUrlAddMode = (
  campaignTypeOptions: FormOption[],
  portalUrl: string | undefined | null,
  customJobPostingUrl: string | undefined | null
): boolean => {
  return (
    isSourcingSharedUrlOnlyMode(campaignTypeOptions) &&
    !portalUrl &&
    !customJobPostingUrl
  );
};

/**
 * Gets the URL to preview (prefers portalUrl, falls back to customJobPostingUrl)
 */
export const getSourcingSharedPreviewUrl = (
  portalUrl: string | undefined | null,
  customJobPostingUrl: string | undefined | null
): string | null => {
  return portalUrl || customJobPostingUrl || null;
};
